import moment from 'moment';

import {DateFormat} from './typedef';

const isOnlyDigit = (str: string) => str.match(/^\d+$/);

const getFormattedYear = (str: string): number | null => {
    let number = Number(str);

    if (number < 1000) {
        number += 2000;
    }

    return str.length > 4 ? null : number;
};

const getValueFormats = (preferencedFormat?: DateFormat): Array<string> => {
    const allAllowedFormats = Object.values(DateFormat);

    return preferencedFormat ? [
        preferencedFormat,
        ...allAllowedFormats.filter(
            format => format !== preferencedFormat,
        ),
    ] : allAllowedFormats;
};

type ConvertStringToDate = (arg0: {
    value: string,
    dateFormat?: DateFormat,
    valueFormat?: DateFormat,
}) => string | null;

/**
 * @function convertStringToDate
 * @description The function is designed to convert text to date string
 * @param {string} value - Text that should be converted to a date string
 * @param {DateFormat} dateFormat - The format to which the value should be converted
 * @param {DateFormat} valueFormat - Value format
 * @example <caption>1. Example of using a number.</caption>
 * // returns '2123-01-01'
 * convertStringToNumber({value: '123', valueFormat: DateFormats.DDMMYYYY});
 * @example <caption>2. Example of using the string.</caption>
 * // returns '2022-12-01'
 * convertStringToNumber({value: '12/22', valueFormat: DateFormats.DDMMYYYY});
 * @example <caption>3. Example of using the date.</caption>
 * // returns '2021-11-22'
 * convertStringToNumber({value: '11.22.2021', valueFormat: DateFormats.MMDDYYYY});
 * @example <caption>4. Example of using the date.</caption>
 * // returns '2022-08-24'
 * convertStringToNumber({value: 'August-24-2022'});
 * @returns {number | null} converted `string` or `null`
 */
export const convertStringToDate: ConvertStringToDate = ({
    value,
    dateFormat = DateFormat.YYYYMMDD,
    valueFormat,
}) => {
    let momentDate;

    if (isOnlyDigit(value)) {
        const year = getFormattedYear(value);
        momentDate = year && moment({year});
    } else if (value.match(/^\d{1,2}[/.-]\d{1,2}$/)) {
        momentDate = moment(value, 'MM-YY');
    } else {
        const formats = getValueFormats(valueFormat);
        momentDate = moment(value, formats);
    }

    return momentDate && momentDate.isValid()
        ? momentDate.format(dateFormat)
        : null;
};
